import { inject, Injectable, SecurityContext, signal, WritableSignal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { MastraClient } from '@mastra/client-js';
import type { AiMessageType } from '@mastra/core';
import { marked } from 'marked';

import { APP_CONFIG, AUTH_METHOD, AUTH_TOKEN, localStorageSignal } from '@core/common-legacy';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { ORG_GUID, type TellentAdminAppConfig } from '@core/tellent-types';
import { generateUuid } from '@core/utils-legacy';

export interface AiMessage extends AiMessageType {
  isPending?: boolean;
  isError?: boolean;
  isComponent?: boolean;
}

@Injectable({ providedIn: 'root' })
export class HrAssistantChatService {
  private readonly token = inject(AUTH_TOKEN);
  private readonly appConfig = inject<TellentAdminAppConfig>(APP_CONFIG);
  private readonly method = inject(AUTH_METHOD);
  private readonly companyGuid = inject(ORG_GUID);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly agentId = 'hrAssistant';
  private readonly mastraClient = new MastraClient({
    baseUrl: `${this.appConfig.tellentApiUrl}/intelligence/org/${this.companyGuid}/assistant/proxy`,
    headers: {
      Authorization: `${this.method} ${this.token}`,
      'Content-Type': 'application/json',
    },
  });

  private readonly currentUserGuid = inject(TellentCurrentUserDataFacade).currentUserGuid;

  public isNewThread = localStorageSignal('hr-assistant-new-thread', false);
  public threads = signal<Record<string, WritableSignal<Array<WritableSignal<AiMessage>>>>>({});
  public currentThreadId = signal('');

  private hrAssistant = this.mastraClient.getAgent(this.agentId);

  public async loadThreads(restoreLastThread: boolean) {
    const threads = await this.mastraClient.getMemoryThreads({
      agentId: this.agentId,
      resourceId: this.currentUserGuid() || '',
    });
    if (threads.length === 0 || !restoreLastThread) {
      this.startNewThread();
    } else if (restoreLastThread) {
      await this.restoreThread(threads[threads.length - 1].id);
    }
  }

  public async sendMessage(value: string) {
    this.isNewThread.set(false);
    const threadId = this.currentThreadId();
    const messages = this.threads()[threadId];
    const userMessage = signal(<AiMessage>{
      role: 'user',
      content: value,
    });
    const assistantPlaceholderMsg = signal(<AiMessage>{
      id: generateUuid(),
      role: 'assistant',
      content: '',
      isPending: true,
    });
    messages.update(v => [...v, userMessage, assistantPlaceholderMsg]);
    try {
      await this.hrAssistant.generate({
        threadId,
        resourceId: this.currentUserGuid()!,
        messages: [userMessage()],
      });
      await this.restoreThread(this.currentThreadId());
    } catch (error) {
      userMessage.update(msg => ({ ...msg, isError: true, id: generateUuid() }));
      messages.update(messages =>
        messages.filter(msg => msg().id !== assistantPlaceholderMsg().id),
      );
    }
  }

  public async retry(message: AiMessage) {
    const threadId = this.currentThreadId();
    const messages = this.threads()[threadId];
    const isRetryCurrentThread = messages().some(msg => msg().id === message.id);
    if (!isRetryCurrentThread) return;

    await this.restoreThread(threadId);
    this.sendMessage(message.content);
  }

  public startNewThread() {
    const uuid = generateUuid();
    this.currentThreadId.set(uuid);
    this.threads.update(threads => ({
      ...threads,
      [uuid]: signal([]),
    }));
  }

  private async restoreThread(threadId: string) {
    this.currentThreadId.set(threadId);
    const thread = this.mastraClient.getMemoryThread(threadId, this.agentId);
    const messages = await thread.getMessages();
    const messagesGrouped = messages.uiMessages.reduce(
      (acc, message) => {
        const prevMessage = acc[acc.length - 1];
        const isComponent = message.content.includes('<component>');
        if (!prevMessage || prevMessage.role !== message.role) {
          return [
            ...acc,
            { ...message, content: this.processMessageContent(message.content), isComponent },
          ];
        }

        // Merge messages from the same role
        const content = this.processMessageContent(
          prevMessage.content ? `${prevMessage.content}\n${message.content}` : message.content,
        );

        return Object.assign([], acc, {
          [acc.length - 1]: {
            ...prevMessage,
            content,
            isComponent,
            toolInvocations: [
              ...(prevMessage.toolInvocations || []),
              ...(message.toolInvocations || []),
            ],
          },
        });
      },
      <AiMessage[]>[],
    );
    this.threads.update(threads => ({
      ...threads,
      [threadId]: signal(messagesGrouped.map(m => signal(m))),
    }));
  }

  private processMessageContent(message: string) {
    const markComponents = message
      .replaceAll('<component>', '[component]')
      .replaceAll('</component>', '[/component]');
    const markdownToHtml = marked(markComponents, { async: false });
    const sanitizedContent = this.domSanitizer.sanitize(SecurityContext.HTML, markdownToHtml) ?? '';
    const final = sanitizedContent
      .replaceAll('[component]', '<tellent-chat-component>')
      .replaceAll('[/component]', '</tellent-chat-component>');

    return final;
  }
}
